/**
 * Email Service for MFA Setup
 * Integrates with Maildev for development and production email sending
 */

import { appApi } from '@/infrastructure/api';

export interface EmailTemplate {
  subject: string;
  htmlContent: string;
  textContent: string;
}

export interface MFASetupEmailData {
  adminEmail: string;
  adminName: string;
  setupToken: string;
  expiresAt: string;
  setupUrl: string;
}

export interface EmailServiceConfig {
  maildevUrl?: string;
  fromEmail: string;
  fromName: string;
}

export class EmailService {
  private config: EmailServiceConfig;

  constructor(config: EmailServiceConfig = {
    maildevUrl: process.env.NEXT_PUBLIC_MAILDEV_URL || 'http://localhost:1080',
    fromEmail: process.env.NEXT_PUBLIC_FROM_EMAIL || 'security@mechagora.com',
    fromName: process.env.NEXT_PUBLIC_FROM_NAME || 'Mechagora Security Team'
  }) {
    this.config = config;
  }

  /**
   * Generate MFA setup email template
   */
  private generateMFASetupTemplate(data: MFASetupEmailData): EmailTemplate {
    const { adminName, setupToken, expiresAt, setupUrl } = data;

    const subject = 'Mechagora - Complete Your MFA Security Setup';

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${subject}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
        }
        .header {
            background: linear-gradient(135deg, #1890ff, #096dd9);
            color: white;
            padding: 30px;
            text-align: center;
            border-radius: 8px 8px 0 0;
        }
        .content {
            background: white;
            padding: 30px;
            border: 1px solid #e8e8e8;
            border-top: none;
        }
        .security-notice {
            background: #fff7e6;
            border-left: 4px solid #fa8c16;
            padding: 15px;
            margin: 20px 0;
            border-radius: 4px;
        }
        .setup-button {
            display: inline-block;
            background: #52c41a;
            color: white;
            padding: 15px 30px;
            text-decoration: none;
            border-radius: 6px;
            font-weight: 600;
            margin: 20px 0;
            text-align: center;
        }
        .token-box {
            background: #f6f6f6;
            border: 1px solid #d9d9d9;
            padding: 15px;
            font-family: 'Courier New', monospace;
            font-size: 18px;
            text-align: center;
            margin: 15px 0;
            border-radius: 4px;
            letter-spacing: 2px;
        }
        .footer {
            background: #f0f0f0;
            padding: 20px;
            text-align: center;
            border-radius: 0 0 8px 8px;
            font-size: 12px;
            color: #666;
        }
        .warning {
            color: #ff4d4f;
            font-weight: 600;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>🔒 MFA Security Setup Required</h1>
        <p>Complete your multi-factor authentication setup</p>
    </div>

    <div class="content">
        <p>Hello <strong>${adminName}</strong>,</p>

        <p>Your administrator account requires multi-factor authentication (MFA) setup to access Mechagora's trading platform. This is a critical security requirement for all top-secret clearance accounts.</p>

        <div class="security-notice">
            <strong>🛡️ Security Notice:</strong> MFA is mandatory for administrator accounts to protect sensitive trading data and financial operations.
        </div>

        <h3>Setup Instructions:</h3>
        <ol>
            <li>Click the secure setup button below</li>
            <li>Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)</li>
            <li>Enter the verification code to confirm setup</li>
            <li>Save your backup codes in a secure location</li>
        </ol>

        <div style="text-align: center;">
            <a href="${setupUrl}" class="setup-button">🔐 Complete MFA Setup</a>
        </div>

        <h3>Alternative Setup:</h3>
        <p>If the button doesn't work, you can manually enter this setup token:</p>
        <div class="token-box">${setupToken}</div>

        <p><strong>Setup URL:</strong><br>
        <code>${setupUrl}</code></p>

        <div class="security-notice">
            <p class="warning">⏰ This setup link expires on: ${new Date(expiresAt).toLocaleString()}</p>
            <p>For security reasons, you must complete setup within 24 hours.</p>
        </div>

        <h3>Need Help?</h3>
        <ul>
            <li>Download Google Authenticator or Authy on your mobile device</li>
            <li>Contact IT Security if you encounter any issues</li>
            <li>Keep your backup codes in a secure, offline location</li>
        </ul>

        <p><strong>Important:</strong> Do not share this email or setup token with anyone. If you did not request this setup, contact security immediately.</p>
    </div>

    <div class="footer">
        <p>Mechagora Trading Platform - Security Team</p>
        <p>This is an automated security message. Do not reply to this email.</p>
        <p>© ${new Date().getFullYear()} Mechagora. All rights reserved.</p>
    </div>
</body>
</html>`;

    const textContent = `
Mechagora - MFA Security Setup Required

Hello ${adminName},

Your administrator account requires multi-factor authentication (MFA) setup to access Mechagora's trading platform.

SECURITY NOTICE: MFA is mandatory for administrator accounts to protect sensitive trading data.

Setup Instructions:
1. Visit: ${setupUrl}
2. Scan the QR code with your authenticator app
3. Enter the verification code to confirm setup
4. Save your backup codes securely

Setup Token: ${setupToken}
Expires: ${new Date(expiresAt).toLocaleString()}

For security reasons, complete setup within 24 hours.

Need help? Contact IT Security.

Important: Do not share this token with anyone.

Mechagora Security Team
`;

    return { subject, htmlContent, textContent };
  }

  /**
   * Send MFA setup email to administrator
   */
  async sendMFASetupEmail(data: MFASetupEmailData): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      console.log('EmailService: Sending MFA setup email to:', data.adminEmail);

      const template = this.generateMFASetupTemplate(data);

      // Send via backend API (which will use Maildev in development)
      const response = await appApi.system.post('/api/v1/auth/send-mfa-setup-email', {
        to: data.adminEmail,
        subject: template.subject,
        html: template.htmlContent,
        text: template.textContent,
        setupToken: data.setupToken
      });

      if (response.success) {
        console.log('EmailService: MFA setup email sent successfully');
        return {
          success: true,
          messageId: response.data?.messageId
        };
      } else {
        console.error('EmailService: Failed to send MFA setup email');
        return {
          success: false,
          error: 'Failed to send email'
        };
      }
    } catch (error) {
      console.error('EmailService: Error sending MFA setup email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Send MFA setup reminder email
   */
  async sendMFASetupReminder(data: MFASetupEmailData): Promise<{ success: boolean; error?: string }> {
    try {
      const reminderData = {
        ...data,
        setupUrl: data.setupUrl + '?reminder=true'
      };

      const template = this.generateMFASetupTemplate(reminderData);
      template.subject = 'Mechagora - MFA Setup Reminder (Action Required)';

      const response = await appApi.system.post('/api/v1/auth/send-mfa-reminder', {
        to: data.adminEmail,
        subject: template.subject,
        html: template.htmlContent.replace('🔒 MFA Security Setup Required', '⚠️ MFA Setup Reminder - Action Required'),
        text: template.textContent,
        setupToken: data.setupToken
      });

      return {
        success: response.success,
        error: response.success ? undefined : 'Failed to send reminder email'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate MFA setup URL with secure subdomain
   */
  generateMFASetupUrl(adminEmail: string, setupToken: string): string {
    // Use secure MFA subdomain for enhanced security
    const mfaDomain = process.env.NEXT_PUBLIC_MFA_DOMAIN || 'secure.mechagora.com';
    // Always use HTTPS for security, even in development
    const protocol = process.env.NEXT_PUBLIC_SSL_ENABLED === 'false' ? 'http' : 'https';
    // No port needed - proxy handles routing
    const port = '';

    const encodedEmail = encodeURIComponent(adminEmail);
    return `${protocol}://${mfaDomain}${port}/setup?email=${encodedEmail}&token=${setupToken}`;
  }

  /**
   * Validate MFA setup token
   */
  async validateMFASetupToken(email: string, token: string): Promise<{ valid: boolean; expired?: boolean; error?: string }> {
    try {
      const response = await appApi.system.post('/api/v1/auth/validate-mfa-setup-token', {
        email,
        token
      });

      return {
        valid: response.success,
        expired: response.data?.expired,
        error: response.success ? undefined : 'Token validation failed'
      };
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton instance
export const emailService = new EmailService();
